import { action, observable, runInAction, computed } from 'mobx';
import { AuthStore, CategoryStore } from './';
import {
  Category,
  SearchQuery,
  SearchResult,
  Video,
  VIDEO_DETAILS_LOADED
} from '../models';
import {
  default as getRepository,
  VideoRepository,
  CategoryStub
} from '../repositories/Video';
import AsyncOperation from './AsyncOperation';

export interface FetchVideoArgs {
  id: number;
  name?: string;
  loadNextAndPrevious: boolean;
  loadRelated: boolean;
  fetchPromise: Promise<void>;
}

export interface CategoryFavourites {
  category: Category;
  videos: Array<Video>;
}

export interface VideoRepositoryOperations {
  categoryVideosLoading: AsyncOperation<void>;
  favouritesLoading: AsyncOperation<void>;
  favouritingVideo: AsyncOperation<void>;
  featuredLoading: AsyncOperation<void>;
  recentlyAddedLoading: AsyncOperation<void>;
  searchVideosLoading: AsyncOperation<void>;
  videoForRouteLoading: AsyncOperation<void>;
  videosLoading: AsyncOperation<void>;
}

export class VideoStore implements VideoRepositoryOperations {
  // NOTE: Use a lazy-evaluated getter to prevent cyclical import issues
  public get repository(): VideoRepository {
    if (!this._repository) {
      this._repository = getRepository();
    }

    return this._repository;
  }

  @observable public videos: Array<Video> | undefined = [];
  @observable public loading: boolean = false;
  @observable public loadError: Error | undefined;

  @observable public searchResults: SearchResult | undefined;

  @observable
  public categoryVideosLoading: AsyncOperation<void> = AsyncOperation.resolved;
  @observable
  public favouritesLoading: AsyncOperation<void> = AsyncOperation.resolved;
  @observable
  public favouritingVideo: AsyncOperation<void> = AsyncOperation.resolved;
  @observable
  public featuredLoading: AsyncOperation<void> = AsyncOperation.resolved;
  @observable
  public recentlyAddedLoading: AsyncOperation<void> = AsyncOperation.resolved;
  @observable
  public searchVideosLoading: AsyncOperation<void> = AsyncOperation.resolved;
  @observable
  public videoForRouteLoading: AsyncOperation<void> = AsyncOperation.resolved;
  @observable
  public videosLoading: AsyncOperation<void> = AsyncOperation.resolved;

  @computed
  public get featuredVideos(): Array<Video> {
    return (this.videos || []).filter(video => video.featured);
  }

  @computed
  public get recentlyAddedVideos(): Array<Video> {
    return (this.videos || []).filter(video => video.recentlyAdded);
  }

  @computed
  public get favourites(): Array<Video> {
    return (this.videos || []).filter(video => video.favourite);
  }

  private _repository: VideoRepository;

  @computed
  public get favouritesByCategory(): Array<CategoryFavourites> {
    return this.favourites
      ? this.favourites.reduce(
          (grouped, favourite) => {
            if (!favourite.rootCategory || !favourite.rootCategory.id) {
              return grouped;
            }

            const categoryId = favourite.rootCategory.id;
            const categoryFavourite =
              grouped[categoryId] ||
              (grouped[categoryId] = {
                category: favourite.rootCategory,
                videos: []
              });

            if (
              !categoryFavourite.videos.find(video => video.id === favourite.id)
            ) {
              categoryFavourite.videos.push(favourite);
            }

            return grouped;
          },
          [] as Array<CategoryFavourites>
        )
      : [];
  }

  public static getNextAndPreviousFeaturedVideos(
    featuredVideos?: Array<Video>,
    video?: Video
  ): {
    next: Video | undefined | null;
    previous: Video | undefined | null;
  } {
    const activeVideoIndex =
      featuredVideos && video ? featuredVideos.indexOf(video) : -1;

    const next =
      featuredVideos && activeVideoIndex >= 0
        ? featuredVideos[activeVideoIndex + 1] || null
        : undefined;

    const previous =
      featuredVideos && activeVideoIndex >= 0
        ? featuredVideos[activeVideoIndex - 1] || null
        : undefined;

    return { next, previous };
  }

  constructor(
    private categoryStore: CategoryStore,
    private authStore: AuthStore
  ) {
    authStore.videoStore = this;
  }

  // TODO: Extract to search store...
  @action
  public fetchVideosForSearch = async (query: SearchQuery): Promise<void> => {
    if (query.isEmpty()) {
      runInAction(() => (this.searchResults = undefined));
      return;
    }

    return this._performFetch(
      'searchVideosLoading',
      async () => {
        const searchVideos = await this.repository.fetchSearchVideos(query);

        this.addVideos(searchVideos);

        runInAction(
          () => (this.searchResults = new SearchResult(searchVideos, query))
        );
      },
      // Always force search video loads to run fresh
      true
    );
  };

  @action
  public fetchVideosForCategory = async (category: Category): Promise<void> => {
    return this._performFetch(
      'categoryVideosLoading',
      async () => {
        const categoryVideos = await this.repository.fetchForCategory(category);

        runInAction(() => (category.videos = categoryVideos));

        this.addVideos(categoryVideos);
      },
      // Always force category video loads to run fresh
      true
    );
  };

  @action
  public fetchFeaturedVideos = async (force: boolean = false) => {
    return this._performFetch(
      'featuredLoading',
      async () => {
        const featuredVideos = await this.repository.fetchFeatured();

        // Mark the videos as featured
        featuredVideos.forEach(video =>
          runInAction(() => (video.featured = true))
        );

        this.addVideos(featuredVideos);
      },
      force
    );
  };

  @action
  public fetchRecentlyAddedVideos = async (force: boolean = false) => {
    return this._performFetch(
      'recentlyAddedLoading',
      async () => {
        const recentlyAddedVideos = await this.repository.fetchRecentlyAdded();

        // Flag each video as recently added
        recentlyAddedVideos.forEach(video =>
          runInAction(() => (video.recentlyAdded = true))
        );

        this.addVideos(recentlyAddedVideos);
      },
      force
    );
  };

  @action
  public fetchFavourites = async (force: boolean = false) => {
    if (!this.authStore.currentUser) {
      // Can't fetch favourites if no user
      return;
    }

    return this._performFetch(
      'favouritesLoading',
      async () => {
        const recentlyAddedVideos = await this.repository.fetchFavourites();
        this.addVideos(recentlyAddedVideos);
      },
      force
    );
  };

  @action
  public fetchVideoForRoute = async (id: number): Promise<void> => {
    return this._performFetch(
      'videoForRouteLoading',
      async () => {
        await this.fetchVideo(id);
      },
      true
    );
  };

  @action
  public fetchVideo = async (id: number): Promise<void> => {
    if (
      (this.videos || []).find(
        video => video.id === id && video[VIDEO_DETAILS_LOADED]
      )
    ) {
      return;
    }

    this.addVideo(await this.repository.fetchVideo(id));
  };

  @action
  public async reloadAll(): Promise<void> {
    // Reload all of our current videos
    const oldVideos = (this.videos || []).slice();

    runInAction(() => (this.videos = []));

    await Promise.all(oldVideos.map(oldVideo => this.fetchVideo(oldVideo.id)));

    // Also refresh favourites, featured, and recently added videos if needed
    await this.fetchFavourites(true);

    if (this.favouritesLoading) {
      await this.fetchFeaturedVideos(true);
    }

    if (this.recentlyAddedLoading) {
      await this.fetchRecentlyAddedVideos(true);
    }

    return;
  }

  public async saveVideoEvent(
    video: Video,
    eventName: string,
    timestamp: string
  ): Promise<void> {
    return this.repository.saveVideoEvent(video, eventName, timestamp);
  }

  @action
  public async favouriteVideo(video: Video): Promise<void> {
    if (this.favouritingVideo && this.favouritingVideo.running) {
      // Prevent calls while requests are in flight
      return;
    }

    return this._performFetch(
      'favouritingVideo',
      async () => {
        if (video.favourite) {
          await this.repository.unfavouriteVideo(video);
        } else {
          await this.repository.favouriteVideo(video);
        }

        runInAction(() => (video.favourite = !video.favourite));
      },
      // Always force favourites to send
      true
    );
  }

  @action
  private _performFetch = async <K extends keyof VideoRepositoryOperations>(
    operationKey: K,
    fetchOperation: () => Promise<void>,
    force: boolean = false
  ): Promise<void> => {
    const repoOperations = this as VideoRepositoryOperations;

    if (
      !force &&
      repoOperations[operationKey] &&
      repoOperations[operationKey] !== AsyncOperation.resolved
    ) {
      return repoOperations[operationKey].promise;
    }

    const operation = new AsyncOperation<void>(fetchOperation());
    repoOperations[operationKey] = operation;

    return operation.promise;
  };

  @action
  private addVideo = (video: Video) => {
    // Fix stub categories
    if (video.category instanceof CategoryStub) {
      const categoryId = video.category.id;

      video.category = this.categoryStore.allCategories.find(
        category => category.id === categoryId
      );
    }

    runInAction(() => (this.videos = this.videos || []));
    const { videos } = this as { videos: Array<Video> };

    // Prevent inserting duplicates
    const existingIndex = videos.findIndex(v => v.id === video.id);

    if (existingIndex < 0) {
      videos.push(video);
      return;
    }

    const mergeableProperties = [
      'category',
      'favourite',
      'featured',
      'recentlyAdded'
    ];

    // Only replace existing item if it hasn't had details loaded
    const existing = videos[existingIndex];
    if (existing[VIDEO_DETAILS_LOADED]) {
      // TODO: Better merging new data with existing detail item..?
      this.mergeProperties(existing, video, mergeableProperties);

      return;
    }

    this.mergeProperties(video, existing, mergeableProperties);

    videos.splice(existingIndex, 1, video);
  };

  private addVideos = (videos: Array<Video>) => {
    videos.forEach(video => this.addVideo(video));
  };

  private mergeProperties(
    target: object,
    source: object,
    properties: Array<string>
  ): void {
    for (let property of properties) {
      target[property] =
        source[property] === undefined ? target[property] : source[property];
    }
  }
}

export default VideoStore;



// WEBPACK FOOTER //
// ./src/app/stores/VideoStore.ts